// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import { type MontecarloSimulationResp } from '@shared/data-access/interfaces/api/ai/montecarlo';
import { type PortfolioAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  PageContainer,
  PopupButton,
  SectionContainer,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSimulation } from '../hooks';
import { InputParameter } from './input-parameter';
import { SimulationChart } from './simulation-chart';
import { SimulationTable } from './simulation-table';

interface SimulationProps {
  analysis: PortfolioAnalysis;
  defaultSimulation: MontecarloSimulationResp[];
}

export const Simulation: FC<SimulationProps> = ({
  analysis,
  defaultSimulation,
}) => {
  const { t } = useTranslation();
  const {
    handleAnalyze,
    handleReset,
    chartAmount,
    simulation,
    loading,
    simulationParameters,
  } = useSimulation({ analysis });

  const simulationToShow = useMemo(
    () => (simulation.length > 0 ? simulation : defaultSimulation),
    [simulation, defaultSimulation],
  );

  return (
    <PageContainer className="!mx-0 !p-0">
      <div className="mt-5 grid gap-4">
        <SectionContainer
          title={
            <Translation i18nKey="simulation.edit-parameters" ns="components" />
          }
          variant="h3"
        >
          <div className="mt-4 grid grid-flow-col grid-cols-2 grid-rows-4 gap-x-12 gap-y-4">
            {simulationParameters.map(
              ({
                name,
                value,
                onChangeFunction,
                inputKey,
                symbol,
                max,
                min,
                popup,
              }) => (
                <div className="grid grid-cols-[1fr_235px]" key={name}>
                  <div className="flex">
                    <p className="self-center font-semibold text-title-dark">
                      {name}
                    </p>
                    {popup && (
                      <PopupButton popupName={`simulation.${popup}`} tooltip />
                    )}
                  </div>
                  <InputParameter
                    value={value}
                    onChange={onChangeFunction}
                    inputKey={inputKey}
                    symbol={symbol}
                    max={max}
                    min={min}
                  />
                </div>
              ),
            )}
            <div className="flex justify-end space-x-4">
              <Button variant="outlined" color="secondary" onClick={handleReset}>
                <Translation i18nKey="common.reset" ns="common" />
              </Button>
              <Button color="secondary" onClick={handleAnalyze} disabled={loading}>
                {loading
                  ? t('common.analyzing', { ns: 'common' })
                  : t('common.analyze', { ns: 'common' })}
              </Button>
            </div>
          </div>
        </SectionContainer>

        <SectionContainer
          title={
            <Translation i18nKey="simulation.performance-summary" ns="components" />
          }
        >
          <hr className="my-4 h-px border-none bg-border-sep" />
          <SimulationTable simulation={simulationToShow} />
        </SectionContainer>
        <SimulationChart
          data={simulationToShow}
          name={t('research.returns-pct', { ns: 'research' }) as string}
          amount={chartAmount}
        />
      </div>
    </PageContainer>
  );
};
